import * as React from 'react';
import {
    useParams,
    useNavigate,
    Navigate
} from 'react-router-dom';
import * as classNames from 'classnames';
import { LeanSessionMetadata, LeanSessionMetadataTable } from '../Shared/SessionMetadataTable';
import SpirometryResultsTable from '../Spirometry/SpirometryResultsTable';
import { OverreadStatusEnum, FixedCommentMutation, TestAcceptabilityMutation, OverreadChallengeStatusEnum, FvcTestFlow, FvcDataPoint } from "../../misc/api/apiTypes";
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { ToggleSessionFixedCommentsVisibilityAction } from '../../PulmonaryFunctionTests/Shared/UseFixedComments';
import { SpirometryResults } from '../../PulmonaryFunctionTests/Shared/PFTAPITypes';
import { SpirometryAnalysisSetTypeEnum } from '../../misc/api/ApiSpirometryAnalysisSetType';
import { DoneLoadingBestValuesAction, LoadingBestValuesAction, UpdateRepeatabilityAction } from '../Shared/UseAcceptability';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { FixedCommentSetTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetTypes';
import { FixedCommentEntityTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetEntityTypes';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { SessionNote, PefSession, PefAnalysisSet } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { ChallengeAlert } from '../Shared/ChallengeAlert';
import { extractFirstCycle, extractExpiratoryCurveData, adjustTimeToTZero } from "../Spirometry/FvcSessionAnalyzer";
import { useSecurityContext } from '../../Core/SecurityContext';
import { SpirometryGraphs, FvcGraphDataMapping } from '../Spirometry/SpirometryGraphs';
import { SummaryPanel } from '../Shared/SummaryPanel';
import { useGraphs } from '../Shared/UseGraphs';
import { UpdateBestTestNextGenAction, UpdatePefBestTestNextGenAction, useAcceptabilityNextGen } from '../Shared/UseAcceptabilityNextGen';
import { LoadAnalysisSetSelectionsAction, useNextGenFixedComments } from '../Spirometry/Testing/UseNextGenFixedComments';
import { OverreadingAcceptabilityDashboard } from '../Shared/OverreadingAcceptabilityDashboard';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { GoBackToList } from '../Shared/AnalysisHelpers';
import { ParameterTypeName } from '../../misc/api/ParameterTypes';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { ModalSize } from '../../misc/components/modal';

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: "view" | "overread";
}

export interface GraphTestData extends FvcTestFlow {
    ExpiratoryOnlyData: FvcDataPoint[];
    TZeroAdjustedExpiratoryOnlyData: FvcDataPoint[];
    TZeroAdjustedData: FvcDataPoint[];
    Valid: boolean;
}



export const PefAnalysisScreen = (): JSX.Element => {
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUser, currentUserPermissions } = useSecurityContext();

    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>(null);
    const [analysisSet, setAnalysisSet] = React.useState<SpirometryResults>(null);
    const [flowData, setFlowData] = React.useState<GraphTestData[]>([]);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>([]);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const client = useWebPortalApiClient();
    const [technicianTestCommentsAvailable, setTechnicianTestCommentsAvailable] = React.useState(false);
    const [technicianSessionCommentsAvailable, setTechnicianSessionCommentsAvailable] = React.useState(false);
    const [isShowingExpiratoryOnly, setIsShowingExpiratoryOnly] = React.useState(false);
    const [adjustedToTZero, setAdjustedToTZero] = React.useState(false);
    const [subjectAge, setSubjectAge] = React.useState<number>();
    const [studyPefRelevantParameters, setStudyPefRelevantParameters] = React.useState(null);
    const [pefSession, setPefSession] = React.useState<PefSession>();
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<PefAnalysisSet>();
    const [siteAnalysisSet, setSiteAnalysisSet] = React.useState<PefAnalysisSet>();
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const { countries } = useReferenceContextProvider();
    const isOverread = viewState === "overread";
    const [acceptabilityState, acceptabilityDispatch] = useAcceptabilityNextGen(overreadAnalysisSet, siteAnalysisSet);
    const legacyClient = useWebPortalLegacyClient();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getPefAnalysisSet(analysisSetId, "canEdit");

            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadPefSession);

            if (access.canEdit && isOverread && overreadPermission) {
                setIsViewOnly(false);
                await legacyClient.startPefOverread(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const settings = await legacyClient.getStudyPefSettings();
            setStudyPefRelevantParameters(settings.SelectedTestParameters);

            const results = await legacyClient.getPefResultData(analysisSetId);
            setAnalysisSet(results);

            const pefSessionResult = await client.client.getPefSession(results.SessionId, undefined, "tests,visit($expand=subject($expand=site($select=id,name,country,number)))");
            setPefSession(pefSessionResult);

            const analysisSetResult = await client.client.getPefAnalysisSets(undefined, `sessionId eq ${results.SessionId}`, undefined, "tests($expand=test)");

            const sessionMetadataToSet = {
                Id: pefSessionResult.id,
                SiteId: pefSessionResult.visit.subject.site.id,
                SiteName: pefSessionResult.visit.subject.site.name,
                SiteCountryCode: pefSessionResult.visit.subject.site.country,
                SiteCountryName: countries.find(c => c.id === pefSessionResult.visit.subject.site.country)?.name,
                SiteNumber: pefSessionResult.visit.subject.site.number,
                Technician: pefSessionResult.technician,
                SubjectId: pefSessionResult.visit.subject.id,
                SubjectNumber: pefSessionResult.visit.subject.number,
                VisitName: pefSessionResult.visit.name,
                SessionName: pefSessionResult.name,
                SessionDateTime: pefSessionResult.occurred,
                SessionTypeId: pefSessionResult.sessionType,
                ReceivedDateTime: pefSessionResult.created,
                SessionTypeName: pefSessionResult.sessionType,
                Status: null,
                StatusId: null,
                Overreader: null,
                AnalysisSets: analysisSetResult.value.map(a => ({
                    Id: a.id,
                    Analyst: {
                        Username: a.analystUserName
                    },
                    Status: {
                        Name: a.status
                    },
                    Type: {
                        Id: a.type
                    },
                })),
                ChallengeStatusId: pefSessionResult.overreadChallengeStatus,
                ChallengeIsClosed: pefSessionResult.challengeIsClosed
            };
            setSessionMetadata(sessionMetadataToSet);

            const currentAnalysisSet = sessionMetadataToSet.AnalysisSets.find(a => a.Id === analysisSetId);
            const isQc = currentAnalysisSet.Type.Id === SpirometryAnalysisSetTypeEnum.QC;

            const overreadAnalysisSetId = isOverread || isQc ? analysisSetId : sessionMetadataToSet.AnalysisSets.find(a => a.Type.Id === SpirometryAnalysisSetTypeEnum.Overread).Id;
            const overreadAnalysisSetResult = await client.client.getPefAnalysisSet(overreadAnalysisSetId, undefined, "tests($expand=test)");
            setOverreadAnalysisSet(overreadAnalysisSetResult);

            const siteAnalysisSetId = sessionMetadataToSet.AnalysisSets.find(a => a.Type.Id === SpirometryAnalysisSetTypeEnum.Site).Id;
            const siteAnalysisSetResult = await client.client.getPefAnalysisSet(siteAnalysisSetId, undefined, "tests($expand=test)");
            setSiteAnalysisSet(siteAnalysisSetResult);

            fixedCommentDispatchOverreading(LoadAnalysisSetSelectionsAction.create(overreadAnalysisSetResult.fixedComments, overreadAnalysisSetResult.tests));
            fixedCommentDispatchTechnician(LoadAnalysisSetSelectionsAction.create(siteAnalysisSetResult.fixedComments, siteAnalysisSetResult.tests));

            const graphDataMapped = FvcGraphDataMapping(pefSessionResult);
            setFlowData(graphDataMapped);

            const ageResponse = await client.client.getSubject(sessionMetadataToSet.SubjectId, "age");
            setSubjectAge(ageResponse.age);

            await fetchSessionNotes(results.SessionId);

            const studySets = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id}`, undefined, "fixedCommentSets");

            setTechnicianSessionCommentsAvailable(studySets.value.find(at => at.commentSetType === FixedCommentSetTypeEnum.Technician && at.sessionType === SessionTypes.Pef)?.fixedCommentSets.find(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Session) !== null);
            setTechnicianTestCommentsAvailable(studySets.value.find(at => at.commentSetType === FixedCommentSetTypeEnum.Technician && at.sessionType === SessionTypes.Pef)?.fixedCommentSets.find(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Test) !== null);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    const [graphState, graphDispatch] = useGraphs(flowData);
    const [fixedCommentStateTechnician, fixedCommentDispatchTechnician] = useNextGenFixedComments(SessionTypes.Pef, FixedCommentSetTypeEnum.Technician, siteAnalysisSet);
    const [fixedCommentStateOverreading, fixedCommentDispatchOverreading] = useNextGenFixedComments(SessionTypes.Pef, FixedCommentSetTypeEnum.Overread, overreadAnalysisSet);

    React.useEffect(() => {
        const fetchBestValues = async () => {
            acceptabilityDispatch(LoadingBestValuesAction.create());

            const bestTests = await legacyClient.calculatePefBestTestsForAnalysisSet(acceptabilityState.analysisSet, siteAnalysisSet, pefSession);

            acceptabilityDispatch(UpdatePefBestTestNextGenAction.create(
                bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_PEFMIN).Value,
                bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_PEFSEC).Value,
            ));

            const repeatabilityValues = await legacyClient.calculatePefRepeatabilityForAnalysisSet(acceptabilityState.analysisSet, pefSession);
            acceptabilityDispatch(UpdateRepeatabilityAction.create(repeatabilityValues));
            acceptabilityDispatch(DoneLoadingBestValuesAction.create());
        }

        if (acceptabilityState.analysisSet !== undefined && pefSession !== undefined) {
            fetchBestValues();
        }
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch, subjectAge]);

    const graphData: GraphTestData[] = React.useMemo(() => {
        if (!(graphState.graphData && sessionMetadata && analysisSet && fixedCommentStateOverreading.areAvailableFixedCommentsLoaded !== null))
            return;

        return graphState.graphData.map(vt => {
            try {
                const sampleRate1 = Math.abs(vt.VolumeData[0].Time - vt.VolumeData[1].Time).toFixed(2);
                const sampleRate2 = Math.abs(vt.VolumeData[5].Time - vt.VolumeData[6].Time).toFixed(2);

                // TODO: The curve extraction code cannot work with an inconsistent sample rate, cut this logic short for now
                if (sampleRate1 !== sampleRate2) {
                    return {
                        TestId: vt.TestId,
                        TestNumber: vt.TestNumber,
                        ColorHex: vt.ColorHex,
                        IsDisplayed: vt.IsDisplayed,
                        VolumeData: vt.VolumeData,
                        ExpiratoryOnlyData: vt.VolumeData,
                        TZeroAdjustedExpiratoryOnlyData: vt.VolumeData,
                        TZeroAdjustedData: vt.VolumeData,
                        Valid: vt.Valid
                    }
                }
                const firstCycle = extractFirstCycle(vt.VolumeData);
                const expiratoryCycle = extractExpiratoryCurveData(vt.VolumeData, firstCycle);

                const expiratoryOnlyData = vt.VolumeData.slice(expiratoryCycle.startOfTestIndex, expiratoryCycle.endOfTestIndex);
                const tZeroAdjustedDataFull = adjustTimeToTZero(vt.VolumeData, expiratoryCycle);
                const tZeroAdjustedDataExpiratoryOnlyData = adjustTimeToTZero(expiratoryOnlyData, expiratoryCycle);

                return {
                    TestId: vt.TestId,
                    TestNumber: vt.TestNumber,
                    ColorHex: vt.ColorHex,
                    IsDisplayed: vt.IsDisplayed,
                    VolumeData: vt.VolumeData,
                    ExpiratoryOnlyData: expiratoryOnlyData,
                    TZeroAdjustedExpiratoryOnlyData: tZeroAdjustedDataExpiratoryOnlyData,
                    TZeroAdjustedData: tZeroAdjustedDataFull,
                    Valid: vt.Valid
                }
            } catch (ex) {
                return {
                    TestId: vt.TestId,
                    TestNumber: vt.TestNumber,
                    ColorHex: vt.ColorHex,
                    IsDisplayed: vt.IsDisplayed,
                    VolumeData: vt.VolumeData,
                    ExpiratoryOnlyData: [],
                    TZeroAdjustedExpiratoryOnlyData: [],
                    TZeroAdjustedData: [],
                    Valid: vt.Valid
                };
            }
        })
    }, [graphState.graphData, sessionMetadata, analysisSet, fixedCommentStateOverreading.areAvailableFixedCommentsLoaded]);

    if (!(flowData.length > 0 && sessionMetadata && analysisSet && acceptabilityState.analysisSet && siteAnalysisSet && fixedCommentStateOverreading.areAvailableFixedCommentsLoaded !== null)) {
        return (<LoadingWithMessage message="Loading PEF Data..." />);
    }

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.AddPefSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.ViewPefSessionNotes);

    const pageAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId);
    const pageAnalysisSetType = pageAnalysisSet.Type.Id;
    const isQcAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = pageAnalysisSetType === SpirometryAnalysisSetTypeEnum.Overread;
    const isUnauthorized = isQcAnalysisSet && pageAnalysisSet.Analyst.Username && pageAnalysisSet.Analyst.Username != currentUser.Username;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    if (isOverread && isSiteAnalysisSet) {
        return <Navigate to={{ pathname: `/Pef/Analysis/${analysisSetId}/view` }} />;
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentStateOverreading.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentStateOverreading.testFixedComments.map(x => ({
            TestId: x.testId, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.analysisSet.tests.map(x => ({
            TestId: x.testId,
            Accepted: x.accepted ?? false
        } as TestAcceptabilityMutation));

        try {

            const response2 = await legacyClient.updateAcceptabilityPef(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, acceptabilityState.analysisSet, siteAnalysisSet, pefSession, annotation);

            if (response2.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(navigate, currentUserPermissions, "/sessions", PermissionEnum.OverreadPefSession, "/pef/analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const overreadingDisabled = viewState === "overread"
        && (fixedCommentStateOverreading.areAvailableFixedCommentsLoaded === false || (fixedCommentStateOverreading.areFixedCommentsLoaded === undefined || fixedCommentStateOverreading.areFixedCommentsLoaded === null || fixedCommentStateOverreading.areFixedCommentsLoaded === false));

    const tidalToggleClasses = classNames(
        'fas',
        'fa-fw',
        {
            'fa-toggle-on': isShowingExpiratoryOnly,
            'fa-toggle-off': !isShowingExpiratoryOnly
        }
    );

    const tZeroToggleClasses = classNames(
        'fas',
        'fa-fw',
        {
            'fa-toggle-on': adjustedToTZero,
            'fa-toggle-off': !adjustedToTZero
        }
    );

    return (
        <React.StrictMode>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQcAnalysisSet && isSiteAnalysisSet === false && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={SessionTypes.Pef}
                    onRejectComplete={() => GoBackToList(navigate, currentUserPermissions, "/sessions", PermissionEnum.OverreadPefSession, "/pef/Analysis")}
                /> : null}

            <h2 data-automationid="Pef-OverRead" className="text-primary">PEF Data</h2>
            {!isQcAnalysisSet ?
                <LeanSessionMetadataTable
                    sessionMetadata={sessionMetadata}
                    selectedAnalysisSetId={analysisSetId}
                />
                : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}
            <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="btn-group pull-right">
                                <button className="btn btn-primary" onClick={() => setIsShowingExpiratoryOnly(!isShowingExpiratoryOnly)}>
                                    <i className={tidalToggleClasses} aria-hidden="true"></i>&nbsp;Expiratory Only
                                </button>
                                <button className="btn btn-primary" onClick={() => setAdjustedToTZero(!adjustedToTZero)}>
                                    <i className={tZeroToggleClasses} aria-hidden="true"></i>&nbsp;TZero Adjust
                                </button>
                            </div>
                        </div>
                    </div>
                    <SpirometryGraphs data={graphData} tZeroAdjust={adjustedToTZero} expiratoryOnlyVisible={isShowingExpiratoryOnly} />
                </div>
            </div>

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <SpirometryResultsTable
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        session={pefSession}
                        analysisSet={acceptabilityState.analysisSet}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        graphState={graphState}
                        graphDispatch={graphDispatch}
                        fixedCommentsStateTechnician={fixedCommentStateTechnician}
                        fixedCommentsDispatchTechnician={fixedCommentDispatchTechnician}
                        fixedCommentsStateOverreading={fixedCommentStateOverreading}
                        fixedCommentsDispatchOverreading={fixedCommentDispatchOverreading}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        siteAnalysisSet={siteAnalysisSet}
                        technicianSessionCommentsAvailable={technicianSessionCommentsAvailable}
                        technicianTestCommentsAvailable={technicianTestCommentsAvailable}
                    />
                </div>
            </div>
            {
                hasViewNotePermission ?
                    <SummaryPanel
                        notesButtonDisabled={acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.QC}
                        notesButtonName="Session Notes"
                        showNotesCount={true}
                        isLoading={acceptabilityState.loadingBestValues}
                        isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                        notes={sessionNotes}
                        notesErrorStatus={noteError}
                        modalSize={ModalSize.Large}
                        onAddNote={(annotation) => addNewNote(annotation)}
                        onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
                    />
                    : null
            }
            <OverreadingAcceptabilityDashboard
                analysisSetId={analysisSetId}
                isViewOnly={isViewOnly || overreadingDisabled}
                fixedCommentsDispatch={fixedCommentDispatchOverreading}
                fixedCommentState={fixedCommentStateOverreading}
                acceptabilityState={acceptabilityState}
                sessionType={SessionTypes.Pef}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Name}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatchOverreading(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={() => GoBackToList(navigate, currentUserPermissions, "/sessions", PermissionEnum.OverreadPefSession, "/pef/analysis")}
            />
        </React.StrictMode>
    );
}
